import { durationColor, SIZE_KPI_DAYS } from '../implementationsTypes'

interface DurationBarProps {
  days: number        // active execution days
  max: number         // max active days across all issues (for bar width scaling)
  waitingDays?: number
  size?: string | null
  clockStopped?: boolean
  handoverDate?: string | null
}

export function DurationBar({ days, max, waitingDays = 0, size, clockStopped = false, handoverDate }: DurationBarProps) {
  const threshold = size ? SIZE_KPI_DAYS[size] : undefined
  const color = durationColor(days, threshold)

  // Bar width: if we have a KPI threshold, scale against 1.2× threshold so overruns are visible
  const barMax = threshold ? Math.max(threshold * 1.2, days + 1) : Math.max(max, 1)
  const pct = Math.min((days / barMax) * 100, 100)

  const kpiLabel = !clockStopped && threshold ? kpiStatus(days, threshold) : null

  return (
    <div className="flex flex-col gap-0.5">
      <div className="flex items-center gap-2">
        <div className="relative flex-1 bg-[var(--bg-primary)] rounded h-2 overflow-visible">
          {/* Active days bar */}
          <div className="h-full rounded" style={{ width: `${pct}%`, background: color }} />
          {/* KPI threshold marker */}
          {threshold && (
            <div
              className="absolute top-[-3px] bottom-[-3px] w-[2px] rounded-full opacity-60"
              style={{
                left: `${Math.min((threshold / barMax) * 100, 100)}%`,
                background: '#94a3b8',
              }}
              title={`Target: ${threshold}d`}
            />
          )}
        </div>
        <span className="text-xs font-semibold min-w-[50px] text-right" style={{ color }}>
          {days}d
        </span>
      </div>
      <div className="flex items-center gap-1.5 pl-0">
        {clockStopped ? (
          <span
            className="text-[10px] font-semibold px-1.5 py-0.5 rounded"
            style={{ background: 'rgba(34,197,94,0.12)', color: '#22c55e' }}
          >
            Handed over{handoverDate ? ` ${formatHandoverDate(handoverDate)}` : ''}
          </span>
        ) : kpiLabel && (
          <span
            className="text-[10px] font-semibold px-1.5 py-0.5 rounded"
            style={{ background: kpiLabel.bg, color: kpiLabel.color }}
          >
            {kpiLabel.text}
          </span>
        )}
        {waitingDays > 0 && (
          <span className="text-[10px] text-[var(--text-muted)]/60">
            +{waitingDays}d paused
          </span>
        )}
      </div>
    </div>
  )
}

function formatHandoverDate(dateStr: string): string {
  const d = new Date(dateStr)
  return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })
}

function kpiStatus(activeDays: number, threshold: number): { text: string; color: string; bg: string } {
  const pct = activeDays / threshold
  if (pct < 0.7) return { text: 'On track', color: '#22c55e', bg: 'rgba(34,197,94,0.12)' }
  if (pct < 0.9) return { text: 'Monitor', color: '#f59e0b', bg: 'rgba(245,158,11,0.12)' }
  if (pct < 1.0) return { text: 'At risk', color: '#f97316', bg: 'rgba(249,115,22,0.12)' }
  return { text: 'Over target', color: '#ef4444', bg: 'rgba(239,68,68,0.12)' }
}
